import { Banner, BlockStack, Box, Card, Divider, Text } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '../../../context/useStore';
import AllocationGrid from '../../../shared/components/approvals/AllocationGrid';
import { approvalsApi, DOC_TYPE_META } from '../../../shared/components/approvals/approvalsApi';
import AssigneeModal from '../../../shared/components/approvals/AssigneeModal';
import PurchaseOrderPanel from '../../../shared/components/approvals/PurchaseOrderPanel';
import RejectModal from '../../../shared/components/approvals/RejectModal';
import { useAllocationDraft } from '../../../shared/components/approvals/useAllocationDraft';
import { formatToCurrency } from '../../../shared/components/functions';
import { isAccountingRole } from '../../../utils/userRoles';
import ActionBar from './ActionBar';
import ApprovalTimeline from './ApprovalTimeline';
import ApprovalWorkspace from './ApprovalWorkspace';
import DocumentHeader from './DocumentHeader';
import SupplierHistoryCard from './SupplierHistoryCard';

const Section = ({ title, children }) => (
  <Box paddingBlockStart="300">
    <BlockStack gap="200">
      <Text variant="headingSm" as="h3">
        {title}
      </Text>
      {children}
    </BlockStack>
  </Box>
);

/*
  The approver's card: PDF left, document facts, purchase order(s), the cost split, supplier history and the
  approve / reject / reassign / skip actions. `deck` comes from the queue shell.
*/
export default function ApproveCard({ card, setCard, deck, registerActions, pdfCache, onDecided, setToast, setErrorToast }) {
  const { user } = useAuthStore();
  const isAdmin = isAccountingRole(user?.role);
  const meta = DOC_TYPE_META[card.docType];
  const docId = card.document[meta.pk];
  const draft = useAllocationDraft(card, { currentUserId: user?.id, isAdmin, requireCostType: meta.hasCostType });
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null);
  const [error, setError] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);

  const allMine = draft.lines.length > 0 && draft.lines.every((row) => row.editable);
  const mustBalance = allMine || draft.lines.filter((row) => !row.editable).every((row) => row.Status !== 'PENDING');
  const approveDisabledReason = draft.hasRowErrors ? 'Fix the highlighted lines first' : mustBalance && !draft.isBalanced ? `Allocate the remaining ${formatToCurrency(draft.remaining)} first` : null;

  const run = useCallback(
    async (action, { leaves = false } = {}) => {
      setBusy(true);
      setError(null);
      const response = await action();
      setBusy(false);
      if (!response.ok) {
        setError(response.error || 'Something went wrong');
        if (setErrorToast) setErrorToast(true);
        return response;
      }
      if (setToast) setToast(true);
      const stillMine = response.data?.myPendingLineIds?.length > 0;
      if (leaves && !stillMine) {
        if (onDecided) onDecided(response.data);
        deck.removeCurrent();
      } else {
        setCard(response.data);
      }
      return response;
    },
    [deck, onDecided, setCard, setErrorToast, setToast]
  );

  const approve = useCallback(() => {
    if (approveDisabledReason || busy) return;
    run(() => approvalsApi.approve(card.docType, docId, { lines: draft.dirty ? draft.toPayload() : undefined }), { leaves: true });
  }, [approveDisabledReason, busy, card.docType, docId, draft, run]);

  const saveSplit = useCallback(() => run(() => approvalsApi.saveAllocations(card.docType, docId, draft.toPayload(), null)), [card.docType, docId, draft, run]);

  useEffect(() => {
    if (registerActions) registerActions({ approve, reject: () => !busy && setModal({ type: 'reject' }), skip: deck.skip });
  }, [approve, busy, deck.skip, registerActions]);

  const actions = useMemo(
    () => [
      { id: 'rejectAction', content: 'Reject', tone: 'critical', onAction: () => setModal({ type: 'reject' }), disabled: busy || !card.permissions.canReject, shortcut: 'R' },
      { id: 'reassignAction', content: 'Reassign', onAction: () => setModal({ type: 'reassign' }), disabled: busy || !card.permissions.canReassign },
      ...(draft.dirty ? [{ id: 'saveAllocation', content: 'Save split', onAction: saveSplit, disabled: busy || draft.hasRowErrors, loading: busy }] : []),
      { id: 'skipAction', content: 'Skip', onAction: deck.skip, disabled: busy || deck.total < 2, shortcut: 'S' },
      {
        id: 'approveAction',
        content: allMine ? 'Approve' : 'Approve my lines',
        variant: 'primary',
        onAction: approve,
        disabled: busy || Boolean(approveDisabledReason) || !card.permissions.canApprove,
        disabledReason: approveDisabledReason,
        loading: busy,
        shortcut: 'A'
      }
    ],
    [allMine, approve, approveDisabledReason, busy, card.permissions, deck.skip, deck.total, draft.dirty, draft.hasRowErrors, saveSplit]
  );

  return (
    <>
      <ApprovalWorkspace
        resource={meta.apiResource}
        s3Keys={card.s3Keys}
        selectedKey={selectedKey}
        onSelectKey={setSelectedKey}
        pdfCache={pdfCache}
        footer={<ActionBar actions={actions} hint={approveDisabledReason || (draft.dirty ? 'Unsaved changes in the split' : '')} />}
      >
        <Card>
          <BlockStack gap="300">
            <DocumentHeader card={card} />
            {error ? (
              <Banner tone="critical" onDismiss={() => setError(null)}>
                {error}
              </Banner>
            ) : null}
            <Divider />
            {meta.hasPurchaseOrders ? (
              <Section title={card.purchaseOrders.length ? `Purchase order${card.purchaseOrders.length > 1 ? 's' : ''}` : 'Purchase order'}>
                <PurchaseOrderPanel
                  card={card}
                  canEdit={card.permissions.canLinkPurchaseOrder}
                  busy={busy}
                  onLink={(bestellingId) => run(() => approvalsApi.linkPurchaseOrder(docId, bestellingId))}
                  onUnlink={(bestellingId) => run(() => approvalsApi.unlinkPurchaseOrder(docId, bestellingId))}
                />
              </Section>
            ) : null}
            <Section title="Cost split">
              <AllocationGrid
                draft={draft}
                docType={card.docType}
                canEdit={card.permissions.canEditAllocation}
                onHandOff={(index) => setModal({ type: 'handoff', lineIndex: index })}
                purchaseOrders={card.purchaseOrders}
                prefill={card.suggestions?.prefill}
              />
            </Section>
            <Section title="Previous invoices of this supplier">
              <SupplierHistoryCard history={card.supplierHistory} />
            </Section>
            <Section title="History">
              <ApprovalTimeline events={card.events} />
            </Section>
          </BlockStack>
        </Card>
      </ApprovalWorkspace>

      <AssigneeModal
        open={modal?.type === 'reassign' || modal?.type === 'handoff'}
        mode={modal?.type || 'reassign'}
        suggestions={(card.suggestions?.assignees || []).filter((s) => s.userId !== user?.id)}
        lines={draft.lines}
        defaultLineIds={modal?.type === 'handoff' && modal.lineIndex !== undefined ? [String(draft.lines[modal.lineIndex]?.id ?? draft.lines[modal.lineIndex]?._key)] : null}
        busy={busy}
        onClose={() => setModal(null)}
        onConfirm={async ({ toUserId, note, lineIds }) => {
          if (modal.type === 'handoff') {
            const target = (card.suggestions?.assignees || []).find((s) => s.userId === toUserId);
            draft.lines.forEach((row, index) => {
              if (lineIds.includes(String(row.id ?? row._key))) draft.update(index, { approverId: toUserId, approverAfkorting: target?.Afkorting || null });
            });
            setModal(null);
            return;
          }
          const response = await run(() => approvalsApi.reassign(card.docType, docId, { toUserId, note }), { leaves: true });
          if (response.ok) setModal(null);
        }}
      />
      <RejectModal
        open={modal?.type === 'reject'}
        busy={busy}
        onClose={() => setModal(null)}
        summary={`${meta.label} ${card.document[meta.numberField]} · ${card.supplier?.NAAM || card.document.Leveranciernaam || ''} · ${formatToCurrency(card.allocation.basis)}`}
        onConfirm={async (reason) => {
          const response = await run(() => approvalsApi.reject(card.docType, docId, reason), { leaves: true });
          if (response.ok) setModal(null);
        }}
      />
    </>
  );
}
